import React from 'react';
import { usePageContext } from 'vike-react/usePageContext';

import { SsrTimer, profileSsr } from '../../server/timing';

/**
 * Wrap a component to measure its SSR render time (component + children) and
 * emit a Server-Timing span. No-op on the client.
 *
 * Usage:
 *   export default withSsrTiming(HomePage, 'HomePage');
 */
export function withSsrTiming<P extends object>(
  Component: React.ComponentType<P>,
  name: string
) {
  function Wrapped(props: P) {
    // Only meaningful during SSR; harmless on the client (timer will be null)
    const pageContext = usePageContext();
    const timer: SsrTimer | null =
      typeof window === 'undefined'
        ? (((pageContext as any).ssrTimer as SsrTimer | undefined) ?? null)
        : null;

    return profileSsr(timer, `cmp:${name}`, () => <Component {...props} />);
  }

  Wrapped.displayName = `withSsrTiming(${Component.displayName || Component.name || 'Component'})`;
  return Wrapped;
}

/** Inline helper for ad-hoc blocks inside server components. */
export function profileSsrBlock<T>(
  name: string,
  fn: () => T,
  pageContext?: any
): T {
  const timer: SsrTimer | null =
    typeof window === 'undefined'
      ? ((pageContext?.ssrTimer as SsrTimer | undefined) ?? null)
      : null;
  return profileSsr(timer, name, fn);
}
